/// <reference path="../quill.d.ts" />
import extend from 'extend';
import Block, { bubbleFormats } from '@cimpress-technology/quill/blots/block';
import Quill from '@cimpress-technology/quill/core';
import Selection from '@cimpress-technology/quill/core/selection';
import Editor from '@cimpress-technology/quill/core/editor';
import Indent from '@cimpress-technology/quill/formats/indent';
import Strike from '@cimpress-technology/quill/formats/strike';
import Keyboard from '@cimpress-technology/quill/modules/keyboard';

import { blockAttributesMapReversed } from './attributes';
import { ListItem } from './formats/list';
import { Paragraph } from './formats/paragraph';
import { Span } from './formats/span';

import Casing, { BlockCasingAttributor } from './attributors/CasingAttributor';
import Color, { BlockColorAttributor } from './attributors/ColorAttributor';
import FontFamily, { BlockFamilyAttributor } from './attributors/FamilyAttributor';
import FontSize, { BlockSizeAttributor } from './attributors/SizeAttributor';
import Letterspacing, { BlockLetterspacingAttributor } from './attributors/LetterspacingAttributor';
import LineHeight, { BlockLineHeightAttributor } from './attributors/LineHeightAttributor';
import StrokeColor, { BlockStrokeColorAttributor } from './attributors/StrokeColorAttributor';
import StrokeOverprints, { BlockStrokeOverprintsAttributor } from './attributors/StrokeOverprintsAttributor';
import StrokeThickness, { BlockStrokeThicknessAttributor } from './attributors/StrokeThicknessAttributor';
import StrokePosition, { BlockStrokePositionAttributor } from './attributors/StrokePositionAttributor';
import OverprintsAttributor, { BlockOverprintsAttributor } from './attributors/OverprintsAttributor';

import { combineNodes, combineFormatsAll, combineFormatsCommon, BlockAttributes } from './combineNodes';

const Parchment = Quill.import('parchment');
const { StyleAttributor, Scope } = Parchment;

const FontWeightAttributor = new StyleAttributor('fontWeight', 'font-weight', { whitelist: ['normal', 'bold'], scope: Scope.INLINE });
const BlockFontWeightAttributor = new StyleAttributor(
    'blockFontWeight',
    'font-weight',
    { whitelist: ['normal', 'bold'], scope: Scope.BLOCK },
);

const FontStyleAttributor = new StyleAttributor('fontStyle', 'font-style', { whitelist: ['normal', 'italic'], scope: Scope.INLINE });
const BlockFontStyleAttributor = new StyleAttributor(
    'blockFontStyle',
    'font-style',
    { whitelist: ['normal', 'italic'], scope: Scope.BLOCK },
);

const TextDecorationAttributor = new StyleAttributor(
    'textDecoration',
    'text-decoration-line',
    { whitelist: ['none', 'underline'], scope: Scope.INLINE },
);
const BlockTextDecorationAttributor = new StyleAttributor(
    'blockTextDecoration',
    'text-decoration-line',
    { whitelist: ['none', 'underline'], scope: Scope.BLOCK },
);

interface IClipboard {
    quill: any;
    new(): IClipboard;
}

function registerPlainClipboard() {
    const Clipboard = Quill.import('modules/clipboard');
    class PlainClipboard extends (Clipboard as IClipboard) {
        public onPaste(range, { text }) {
            if (!this.quill.root.isContentEditable) {
                return;
            }
            const formats = this.quill.getFormat(range.index);
            if (range.length > 0) {
                this.quill.deleteText(range.index, range.length);
            }
            this.quill.insertText(range.index, text, formats);
            this.quill.scrollIntoView();
        }
    }

    Quill.register('modules/clipboard', PlainClipboard, true);
}

function registerCustomFormats() {
    Strike.blotName = 'strikeout'; // to match cimdoc name

    // Any modules being overwritten need to be explicitly indicated as an overwrite or quill will console warn to notify
    Quill.register('blots/block', Paragraph, true);
    Quill.register('blots/inline', Span, true);

    Quill.register({
        'formats/fontWeight': FontWeightAttributor,
        'formats/color': Color,
        'formats/fontFamily': FontFamily,
        'formats/fontSize': FontSize,
        'formats/letterspacing': Letterspacing,
        'formats/casing': Casing,
        'formats/lineHeight': LineHeight,
        'formats/indent': Indent,
        'formats/fontStyle': FontStyleAttributor,
        'formats/listItem': ListItem,
        'formats/strikeout': Strike,
        'formats/textDecoration': TextDecorationAttributor,
        'formats/strokeColor': StrokeColor,
        'formats/strokeOverprints': StrokeOverprints,
        'formats/strokeThickness': StrokeThickness,
        'formats/strokePosition': StrokePosition,
        'formats/overprints': OverprintsAttributor,
        'formats/blockColor': BlockColorAttributor,
        'formats/blockFontFamily': BlockFamilyAttributor,
        'formats/blockFontSize': BlockSizeAttributor,
        'formats/blockCasing': BlockCasingAttributor,
        'formats/blockLineHeight': BlockLineHeightAttributor,
        'formats/blockLetterspacing': BlockLetterspacingAttributor,
        'formats/blockFontWeight': BlockFontWeightAttributor,
        'formats/blockFontStyle': BlockFontStyleAttributor,
        'formats/blockTextDecoration': BlockTextDecorationAttributor,
        'formats/blockStrokeColor': BlockStrokeColorAttributor,
        'formats/blockStrokeOverprints': BlockStrokeOverprintsAttributor,
        'formats/blockStrokeThickness': BlockStrokeThicknessAttributor,
        'formats/blockStrokePosition': BlockStrokePositionAttributor,
        'formats/blockOverprints': BlockOverprintsAttributor,
    });
}

registerPlainClipboard();
registerCustomFormats();

// TODO: There shouldn't be defaults for these. Essentially there should always be a
// value set for these so we don't need the defaults. We can clean it up later
// once all the formatting stuff is fixed.
// original quill function
/* eslint-disable no-param-reassign */
function combineFormats(formats, combined) {
    return Object.keys(combined).reduce((merged, name) => {
        if (formats[name] == null) {
            return merged;
        }
        if (combined[name] === formats[name]) {
            merged[name] = combined[name];
        } else if (Array.isArray(combined[name])) {
            if (combined[name].indexOf(formats[name]) < 0) {
                merged[name] = combined[name].concat([formats[name]]);
            }
        } else {
            merged[name] = [combined[name], formats[name]];
        }
        return merged;
    }, {});
}
/* eslint-enable no-param-reassign */

// Need to ignore an issue with using `this` in the function parameters
// @ts-ignore
Quill.prototype.getCommonFormats = function getCommonFormats(index = this.getSelection(true), length = 0) {
    return this.getFormats(index, length, combineFormatsCommon);
};

// @ts-ignore
Quill.prototype.getAllFormats = function getAllFormats(index = this.getSelection(true), length = 0) {
    return this.getFormats(index, length, combineFormatsAll);
};

// @ts-ignore
Quill.prototype.getFormats = function getFormats(index = this.getSelection(true), length = 0, combiner = combineFormats) {
    if (index == null) {
        return {};
    }
    if (typeof index === 'number') {
        return this.editor.getFormats(index, length, combiner);
    }
    return this.editor.getFormats(index.index, index.length, combiner);
};

/**
 * We've really messed up quill by trying to add duplicate block-level attributors for all our inline formats
 * (which we had to do in order to get Quill to preserve formats on empty lines).
 * This function changes `block` level attributes to the regular one if they are found in a leaf.
 * @param leaf a Quill LeafBlot
 */
function fixupBlockFormatsInLeaf(leaf: any) {
    const formats = bubbleFormats(leaf);
    Object.keys(formats).forEach((key) => {
        const leafBlotKey = blockAttributesMapReversed[key];
        if (blockAttributesMapReversed[key]) {
            formats[leafBlotKey] = formats[key];
            delete formats[key];
        }
    });
    return formats;
}

Editor.prototype.getFormats = function getFormats(index, length = 0, combiner = combineFormats) {
    const blocks: BlockAttributes[] = [];
    if (length === 0) {
        let line: Record<string, any>;
        const leaves: Record<string, any>[] = [];
        this.scroll.path(index).forEach((path) => {
            const [blot] = path;
            if (blot instanceof Block) {
                line = bubbleFormats(blot);
            } else if (blot instanceof Parchment.LeafBlot) {
                leaves.push(fixupBlockFormatsInLeaf(blot));
            }
        });
        blocks.push({
            leaves,
            line: line!,
        });
    } else {
        const lines = this.scroll.lines(index, length);

        let previousLength = 0;
        let previousLine = lines[0].prev;
        if (!previousLine && lines[0] instanceof ListItem) {
            previousLine = lines[0].parent.prev;
        }
        while (previousLine) {
            previousLength += previousLine.length();
            if (previousLine instanceof ListItem && !previousLine.prev) {
                previousLine = previousLine.parent.prev;
            } else {
                previousLine = previousLine.prev;
            }
        }
        // The input index is given with respect to the entire text field, but we want the index in the context of the
        // current line. So the above code is used to iterate over the previous lines in order to determine what the index of the current line is
        let currentIndex = index - previousLength;
        let currentLength = length;
        lines.forEach((line) => {
            blocks.push({
                line: bubbleFormats(line),
                leaves: line.descendants(Parchment.LeafBlot, currentIndex, currentLength)
                    .map((leaf) => fixupBlockFormatsInLeaf(leaf)),
            });

            // selection length is allowed to overflow, so don't worry about being exact.
            // last line is the only one that needs to be exact (e.g. less than the length of the line)
            currentLength -= (line.length() - currentIndex);
            // index of subsequent lines after the first are 0 (think multi-line select)
            currentIndex = 0;
        });
    }

    // Need to store block-level formats separate from inline formats
    const [allBlockFormats, allLeafFormats] = combineNodes(blocks, combiner);

    // Combine block and leaf formats at the end, after the `combiner` runs
    return extend.apply(extend, [allBlockFormats, allLeafFormats]);
};

Selection.prototype.handleComposition = function customHandleComposition() {
    this.root.addEventListener('compositionstart', () => {
        this.composing = true;
        // @ts-ignore
        if (window.__IME_COMPATIBILITY_MODE__) { // eslint-disable-line no-underscore-dangle
            // Commenting out batchStart and batchEnd temporarily as a band-aid for Android
            this.scroll.batchStart();
        }
    });
    this.root.addEventListener('compositionend', () => {
        // @ts-ignore
        if (window.__IME_COMPATIBILITY_MODE__) { // eslint-disable-line no-underscore-dangle
            this.scroll.batchEnd();
        }
        this.composing = false;
        if (this.cursor.parent) {
            const range = this.cursor.restore();
            if (!range) return;
            setTimeout(() => {
                this.setNativeRange(
                    range.startNode,
                    range.startOffset,
                    range.endNode,
                    range.endOffset,
                );
            }, 1);
        }
        this.root.addEventListener('compositionupdate', (data) => {
            const reg = /[^\u4e00-\u9fa5a-zA-Z\d]+/;
            // @ts-ignore
            if (reg.exec(data.data) && window.__IME_COMPATIBILITY_MODE__) { // eslint-disable-line no-underscore-dangle
                this.composing = false;
            }
        });
    });
};

// Resolves how lists are autofilled from https://github.com/slab/quill/issues/3953#issuecomment-1891576699 numbered lists will only autoformat with `1.`
Keyboard.DEFAULTS.bindings['list autofill'].prefix = /^\s*?([01]\.|-|\*)$/;

export default Quill;
